import publicClient from '@/api/publicClient'

/**
 * Ensure the value is an array.
 *
 * @param {*} value
 * @return {Array}
 */
function toArray(value) {
	return Array.isArray(value) ? value : []
}

/**
 * Resolve a provider display name from public API eventable fields.
 *
 * @param {Object} eventable
 * @return {String|null}
 */
function resolveEventableName(eventable) {
	return eventable.display_name || eventable.full_name || eventable.name || null
}

/**
 * Normalize a single eventable for FullCalendarEvent display fields.
 *
 * Public events API may return `name` instead of `display_name` / `full_name`.
 *
 * @param {Object} eventable
 * @return {Object}
 */
function normalizeEventable(eventable) {
	if (!eventable || typeof eventable !== 'object') {
		return eventable
	}

	const name = resolveEventableName(eventable)

	return {
		...eventable,
		display_name: eventable.display_name || name,
		full_name: eventable.full_name || name,
	}
}

/**
 * Build a stable unique id for an expanded recurring occurrence.
 *
 * The public events API returns one row per occurrence, all sharing the same
 * event id. Vuex keys events by id, so collisions must be uniquified.
 *
 * @param {Object} event
 * @return {Number|String}
 */
function resolveEventId(event) {
	if (event?.is_recurring && event?.starts_at) {
		return `${event.id}-${event.starts_at}`
	}

	return event.id
}

/**
 * Normalize calendar events for FullCalendarEvent / Vuex.
 *
 * Ensures eventables, requests, and exceptions always exist so model helpers
 * (e.g. hasRequests) do not throw when the public API omits those fields.
 * Expanded recurring occurrences get unique ids and recurrence is cleared so
 * FullCalendar does not expand them again.
 *
 * @param {Array} events
 * @return {Array}
 */
function normalizeCalendarEvents(events) {
	return toArray(events).map(event => ({
		...event,
		id: resolveEventId(event),
		// API already expands occurrences; keep is_recurring for the repeat icon.
		recurrence: null,
		eventables: toArray(event?.eventables).map(normalizeEventable),
		exceptions: toArray(event?.exceptions),
		requests: toArray(event?.requests),
	}))
}

/**
 * Extract an events array from a public API response body.
 *
 * Supports `{ data: [...] }` and a bare array payload.
 *
 * @param {*} payload
 * @return {Array}
 */
function extractEventsPayload(payload) {
	if (Array.isArray(payload?.data)) {
		return payload.data
	}

	if (Array.isArray(payload)) {
		return payload
	}

	return []
}

/**
 * Fetch calendar events for the public share calendar.
 *
 * @param {Number} calendarId
 * @param {String} startsAt
 * @param {String} endsAt
 * @return {Promise<Array>}
 */
export function fetchCalendarEvents(calendarId, startsAt, endsAt) {
	return publicClient
		.get(`v1/calendars/${calendarId}/events`, {
			params: {
				ends_at: endsAt,
				starts_at: startsAt,
			},
		})
		.then(response =>
			normalizeCalendarEvents(extractEventsPayload(response.data))
		)
}
